import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { staffApi } from '@/lib/api';
import ActivationsCard from '@/components/staff/ActivationsCard';
import AssignmentOverviewCard from '@/components/staff/AssignmentOverviewCard';
import { Users, Repeat, Clock, Droplets, UserPlus, AlertTriangle, CalendarClock, XCircle } from 'lucide-react';

function StatCard({ label, value, icon: Icon, testId, to }) {
  const card = (
    <div className="bg-white rounded-xl border border-border/40 p-4 h-full transition-colors duration-150 hover:border-saffron/50">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <p className="text-xs font-medium text-muted-ink uppercase tracking-wide">{label}</p>
        <Icon size={16} className="text-saffron" />
      </div>
      <p className="font-heading text-3xl font-700 text-charcoal mt-2">{value}</p>
    </div>
  );
  if (!to) return <div data-testid={testId}>{card}</div>;
  return (
    <Link to={to} data-testid={testId} className="block">
      {card}
    </Link>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    staffApi.get('/dashboard').then(({ data }) => setStats(data));
  }, []);

  if (!stats) return <p className="text-muted-ink text-sm">Loading dashboard…</p>;

  return (
    <div className="space-y-6">
      <h1 className="font-heading text-2xl font-700 text-charcoal">Dashboard</h1>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Active Subscriptions" value={stats.activeSubscriptions} icon={Repeat} testId="stat-active" to="/admin/subscriptions?status=ACTIVE" />
        <StatCard label="Pending" value={stats.pendingSubscriptions} icon={Clock} testId="stat-pending" to="/admin/subscriptions?smart=pending" />
        <StatCard label="Total Customers" value={stats.totalCustomers} icon={Users} testId="stat-customers" to="/admin/customers" />
        <StatCard label="Redeemed Today" value={stats.tiffinsRedeemedToday} icon={Droplets} testId="stat-redeemed-today" to="/admin/redemptions?range=today" />
        <StatCard label="New This Week" value={stats.newCustomersThisWeek} icon={UserPlus} testId="stat-new-customers" to="/admin/customers?filter=new" />
        <StatCard label="Low Balance" value={stats.lowBalanceCustomers} icon={AlertTriangle} testId="stat-low-balance" to="/admin/subscriptions?smart=lowBalance" />
        <StatCard label="Expiring Soon" value={stats.expiringSoon} icon={CalendarClock} testId="stat-expiring-soon" to="/admin/subscriptions?smart=expiringSoon" />
        <StatCard label="Cancelled" value={stats.cancelledSubscriptions} icon={XCircle} testId="stat-cancelled" to="/admin/subscriptions/cancelled" />
      </div>

      <ActivationsCard />

      <AssignmentOverviewCard />

      <div className="grid md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-border/40 p-4">
          <h2 className="font-heading font-semibold text-charcoal mb-3">Recent Redemptions</h2>
          <div className="space-y-2">
            {stats.recentRedemptions.map((r) => (
              <div key={r.id} className="text-sm flex justify-between border-b border-border/30 pb-2 last:border-0">
                <span className="text-charcoal">{r.subscription?.customer?.name}</span>
                <span className="text-muted-ink">-{r.quantity}</span>
              </div>
            ))}
            {stats.recentRedemptions.length === 0 && <p className="text-sm text-muted-ink">No pickups yet.</p>}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-border/40 p-4">
          <h2 className="font-heading font-semibold text-charcoal mb-3">Recent Subscriptions</h2>
          <div className="space-y-2">
            {stats.recentSubscriptions.map((s) => (
              <div key={s.id} className="text-sm flex justify-between border-b border-border/30 pb-2 last:border-0">
                <span className="text-charcoal">{s.customer?.name}</span>
                <span className="text-muted-ink">{s.plan?.name}</span>
              </div>
            ))}
            {stats.recentSubscriptions.length === 0 && <p className="text-sm text-muted-ink">No subscriptions yet.</p>}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-border/40 p-4">
          <h2 className="font-heading font-semibold text-charcoal mb-3">Recent Customers</h2>
          <div className="space-y-2">
            {stats.recentCustomers.map((c) => (
              <Link key={c.id} to={`/admin/customers/${c.id}`} className="text-sm flex justify-between border-b border-border/30 pb-2 last:border-0 hover:text-saffron transition-colors duration-150">
                <span>{c.name}</span>
                <span className="text-muted-ink">{c.customerCode}</span>
              </Link>
            ))}
            {stats.recentCustomers.length === 0 && <p className="text-sm text-muted-ink">No customers yet.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
